/*
* Displays a comment
* { id: '808b8145-4a56-1f50-b2bc-5c80e0f3b711',
    time: '2022-08-12T21:26:22.1880000Z',
    user: '7365acb9-86b6-cced-004f-1fba4e1b32f6',
    timestamp: 2,
    text: 'First comment that is updated twice' }
* */
import React, { useContext, useEffect, useState, useMemo } from "react";
import {
   ECLRichTextViewer,
   ECLRichTextEditor,
   ECLUserInfo,
   ECLLoader,
} from "../../../../";
import { UserContext, ToastContext } from "../../../../../utilities/contexts";
import { sourceOneRestRequest } from "../../../../../utilities/requests";
import { translate } from "../../../../../utilities/Translate";
import ECLDialog from "../../../../Widgets/ECLDialog/ECLDialog";
import "./CommentsItem.css";

export default function CommentsItem(props) {
   //PROPS:
   //comment -object with id, time, userid#
   //username - string
   //projectId -string
   //sectionId - string
   //getComments - function - callback used to update entire list of comments in ECLComments after a delete or edit

   const [editing, setEditing] = useState(false);
   const [commentText, setCommentText] = useState(props.comment.text);
   const users = useContext(UserContext);
   const addToast = useContext(ToastContext);

   useEffect(() => {
      //The comment object is held and updated in ECLComments.js
      //this keeps it up-to-date here
      setCommentText(props.comment.text);
   }, [props.comment.text]);

   if (!users) {
      return <ECLLoader />;
   }

   async function submitEditedComment(newText) {
      let params = {
         query_type: "comment_write",
         sectionId: props.sectionId,
         commentId: props.comment.id,
         body: newText,
      };
      sourceOneRestRequest(params)
         .then(() => {
            props.getComments();
            setEditing(false);
         })
         .catch((err) => {
            console.error(err);
            addToast("There was an error updating the comment.", "error");
         });
   }

   async function deleteComment() {
      let params = {
         query_type: "comment_delete",
         sectionId: props.sectionId,
         commentId: props.comment.id,
      };
      sourceOneRestRequest(params)
         .then(() => {
            props.getComments();
         })
         .catch((err) => {
            console.error(err);
            addToast("There was an error deleting the comment.", "error");
         });
   }

   useEffect(() => {
      //When the editor opens, start with "Accept Edit" button disabled
      //the user should only be able to "Accept Edit" if they've edited something
      const acceptEditButton = document.getElementById(
         `rich-text-submit-${props.comment.id}`,
      );
      if (acceptEditButton) {
         acceptEditButton.setAttribute("disabled", true);
      }
   }, [editing]);

   const compareText = (newText) => {
      //See if user has edited the text, enable the "Accept Edit" button if they have
      const acceptEditButton = document.getElementById(
         `rich-text-submit-${props.comment.id}`,
      );
      if (newText !== commentText) {
         acceptEditButton.removeAttribute("disabled");
      } else {
         acceptEditButton.setAttribute("disabled", true);
      }
   };

   const date =
      new Date(props.comment.time).toDateString() +
      " " +
      new Date(props.comment.time).toLocaleTimeString();
   const viewer = useMemo(
      () => (
         <ECLRichTextViewer
            textAsMarkdown={commentText}
            project_id={props.projectId}
         />
      ),
      [commentText],
   );

   return (
      <article className={"comment-item comment-item-card"}>
         <div className="left">
            <ECLUserInfo
               user={props.comment.user}
               hideName={false}
            ></ECLUserInfo>
            <div className="bottom">
               {!editing && viewer}
               {
                  //if the current user matches the user who made the comment, edit/delete shows up
                  users.currentUser.username === props.username ? (
                     <div className="edit-delete-section">
                        {!editing && (
                           <>
                              <button onClick={() => setEditing(true)}>
                                 {translate("Edit")}
                              </button>
                              <ECLDialog
                                 title="Delete Comment"
                                 action={deleteComment}
                                 triggerText="Delete"
                              >
                                 <p>
                                    Are you sure you want to delete this
                                    comment?
                                 </p>
                              </ECLDialog>
                           </>
                        )}
                        {editing && (
                           <ECLRichTextEditor
                              uniqueId={props.comment.id}
                              onTextChange={compareText}
                              onSubmit={submitEditedComment}
                              textAreaContent={commentText}
                              onCancel={() => setEditing(false)}
                              submitButtonText={translate("Accept Edit")}
                           />
                        )}
                     </div>
                  ) : null
               }
            </div>
         </div>
         <div className="top">
            <i className="date">{translate(date)}</i>
         </div>
      </article>
   );
}
